import { useState } from 'react';
import type { ChatSession } from '../../types/chat-storage';
import Button from '../atoms/Button';
import IconButton from '../atoms/IconButton';
import AppHeader from './AppHeader';
import HeaderTitle from '../molecules/HeaderTitle';
import SessionWidget from './SessionWidget';
import Spinner from '../atoms/Spinner';

interface ChatSidebarProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isLoadingHistory?: boolean;
}

export function ChatSidebar({
  sessions,
  activeSessionId,
  onSessionSelect,
  onNewChat,
  onDeleteSession,
  onRenameSession,
  isCollapsed,
  onToggleCollapse,
  isLoadingHistory = false,
}: ChatSidebarProps) {
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const handleStartEdit = (session: ChatSession) => {
    setEditingSessionId(session.id);
    setEditTitle(session.title);
  };

  const handleSaveEdit = () => {
    if (editingSessionId && editTitle.trim()) {
      onRenameSession(editingSessionId, editTitle.trim());
    }
    setEditingSessionId(null);
    setEditTitle('');
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditTitle('');
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {return 'Today';}
    if (days === 1) {return 'Yesterday';}
    if (days < 7) {return `${days} days ago`;}
    return date.toLocaleDateString();
  };

  const getMessagePreview = (session: ChatSession) => {
    const lastMessage = session.messages[session.messages.length - 1];
    if (!lastMessage) {return 'No messages yet';}
    
    const textWidget = lastMessage.widgets?.find(w => w.type === 'text');
    if (textWidget) {
      return textWidget.text.slice(0, 60) + (textWidget.text.length > 60 ? '...' : '');
    }
    
    return 'Sent a message';
  };

  const handleSessionEdit = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      handleStartEdit(session);
    }
  };

  const handleSessionDelete = (sessionId: string) => {
    onDeleteSession(sessionId);
  };

  return (
    <div className={`bg-bg-secondary border-r border-border-light flex flex-col h-full transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-80'
    }`}>
      {/* Header */}
      <div className="flex-shrink-0 bg-bg-secondary border-b border-border-light">
        <AppHeader
          variant="secondary"
          size="md"
          hasBorder={false}
          hasShadow={false}
          leftContent={
            <div className="flex items-center space-x-3">
              <IconButton
                onClick={onToggleCollapse}
                variant="ghost"
                size="medium"
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                icon={
                  <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isCollapsed ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    )}
                  </svg>
                }
              />
              {!isCollapsed && (
                <div className="flex items-center space-x-2">
                  <HeaderTitle size="md">Chats</HeaderTitle>
                  {isLoadingHistory && (
                    <Spinner size="sm" variant="primary" />
                  )}
                </div>
              )}
            </div>
          }
          rightContent={
            !isCollapsed ? (
              <IconButton
                onClick={onNewChat}
                variant="key"
                size="medium"
                title="New chat"
                aria-label="New chat"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                }
              />
            ) : undefined
          }
        />
      </div>

      {/* Scrollable Chat List */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {sessions.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-bg-surface flex items-center justify-center">
                <svg className="w-6 h-6 text-text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-text-primary mb-1">No chats yet</h3>
              <p className="text-xs text-text-muted mb-4">Start a conversation with your coach</p>
              <Button
                onClick={onNewChat}
                variant="tertiary"
                size="medium"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                }
                iconPosition="left"
              >
                Start your first chat
              </Button>
            </div>
          ) : (
            <div className="px-3 py-2 space-y-1">
              {sessions.map((session) => (
                <SessionWidget
                  key={session.id}
                  session={session}
                  isActive={activeSessionId === session.id}
                  isEditing={editingSessionId === session.id}
                  editValue={editTitle}
                  onSelect={() => onSessionSelect(session.id)}
                  onEdit={() => handleSessionEdit(session.id)}
                  onDelete={() => handleSessionDelete(session.id)}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onEditValueChange={setEditTitle}
                  getMessagePreview={getMessagePreview}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collapsed New Chat Button */}
      {isCollapsed && (
        <AppHeader
          variant="secondary"
          size="md"
          hasBorder
          hasShadow={false}
          className="border-t border-border-separator"
          leftContent={
            <IconButton
              onClick={onNewChat}
              variant="key"
              size="medium"
              title="New chat"
              aria-label="New chat"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              }
            />
          }
        />
      )}
    </div>
  );
}